// ============================================================
//  ALTIMO Medical Center — Farmacia.jsx  (v1.0)
//  Módulo de Farmacia / Dispensario
//  ✓ Catálogo visual con tarjetas de medicamentos
//  ✓ Búsqueda instantánea + filtros por categoría / alertas
//  ✓ Despacho interno (habitación/quirófano → cargo a cuenta)
//  ✓ Venta externa (descarga directa de inventario)
//  ✓ Carrito multi-ítem con validación de stock
//  ✓ Ticket de despacho imprimible
//  ✓ Historial de movimientos filtrable por fecha
//  ✓ Panel de medicamentos controlados con kardex
//  ✓ Alertas de stock bajo y próximos a vencer
// ============================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../apiClient';
import { useConfirmar } from './ModalConfirmar';
import TooltipAyuda from './TooltipAyuda';

// ─── PALETA ──────────────────────────────────────────────────
const C = {
  azul:     '#1B2A5E',
  verde:    '#1D9E75',
  rojo:     '#dc3545',
  naranja:  '#e67e22',
  gris:     '#6c757d',
  morado:   '#7c3aed',
  azulClaro:'#3b82f6',
  bg:       '#f0f4f8',
  blanco:   '#ffffff',
};

// ─── CATEGORÍAS ───────────────────────────────────────────────
const CATS = {
  'Antibiótico':     { emoji:'🦠', bg:'#fde8e8', color:'#a02020', ring:'#f87171' },
  'AINE':            { emoji:'🔥', bg:'#fff3cd', color:'#856404', ring:'#fbbf24' },
  'Gastroprotector': { emoji:'🫁', bg:'#d1ecf1', color:'#0c5460', ring:'#67e8f9' },
  'Analgésico':      { emoji:'💊', bg:'#fce7f3', color:'#9d174d', ring:'#f472b6' },
  'Solución':        { emoji:'💧', bg:'#cce5ff', color:'#004085', ring:'#60a5fa' },
  'Insumo':          { emoji:'🩺', bg:'#e2e3e5', color:'#383d41', ring:'#9ca3af' },
  'Otro':            { emoji:'⚕️', bg:'#e8f0fb', color:'#1B2A5E', ring:'#818cf8' },
};
const catInfo = (cat) => CATS[cat] || CATS['Otro'];

// ─── HELPERS ─────────────────────────────────────────────────
const hoy        = () => new Date().toISOString().slice(0, 10);
const ahora      = () => new Date().toLocaleString('es-SV');
const fmtFecha   = (f) => f ? new Date(f + 'T00:00:00').toLocaleDateString('es-SV') : '—';
const fmtDinero  = (n) => `$${Number(n || 0).toFixed(2)}`;
const diasHasta  = (f) => f
  ? Math.round((new Date(f + 'T00:00:00') - new Date()) / 86400000)
  : null;

function colorStock(stock, minimo) {
  if (stock <= 0)            return C.rojo;
  if (stock <= minimo)       return C.rojo;
  if (stock <= minimo * 1.5) return C.naranja;
  return C.verde;
}
function colorVenc(fecha) {
  const d = diasHasta(fecha);
  if (d === null) return null;
  if (d < 0)  return C.rojo;
  if (d <= 7) return C.rojo;
  if (d <= 30)return C.naranja;
  return C.verde;
}

// ─── ESTILOS BASE ─────────────────────────────────────────────
const S = {
  page:   { background: C.bg, minHeight:'100vh', fontFamily:'Segoe UI, sans-serif', padding:'0' },
  input:  { width:'100%', fontSize:'13px', padding:'9px 12px', border:'1.5px solid #dde3ea',
            borderRadius:'8px', background:'#fff', outline:'none', boxSizing:'border-box' },
  label:  { display:'block', fontSize:'10px', fontWeight:'700', color:C.azul,
            marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.05em' },
  campo:  { marginBottom:'12px' },
  btn:    (bg, fg='#fff', sz='13px') => ({
    background: bg, color: fg, border:'none', borderRadius:'8px',
    padding:'9px 18px', fontSize: sz, fontWeight:'700', cursor:'pointer',
    fontFamily:'inherit', transition:'opacity .15s',
  }),
  modal:  { position:'fixed', inset:0, background:'rgba(10,20,55,.75)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500, overflowY:'auto' },
  card:   (extra={}) => ({
    background:'#fff', borderRadius:'12px', border:'1px solid #e2e8f0',
    boxShadow:'0 1px 4px rgba(0,0,0,.07)', ...extra,
  }),
  badge:  (color, bg) => ({
    display:'inline-flex', alignItems:'center', gap:'3px',
    padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700',
    background: bg || color+'18', color,
    border: `1px solid ${color}30`,
  }),
  tab:    (activo, color=C.azul) => ({
    padding:'10px 20px', border:'none', cursor:'pointer', borderRadius:'8px',
    fontSize:'13px', fontWeight:'700', fontFamily:'inherit', transition:'all .15s',
    background: activo ? color      : 'transparent',
    color:      activo ? '#fff'     : '#64748b',
  }),
};

// ═════════════════════════════════════════════════════════════
//  TARJETA DE MEDICAMENTO
// ═════════════════════════════════════════════════════════════
function TarjetaMed({ p, onDespachar, onVerKardex }) {
  const cat    = catInfo(p.categoria);
  const cs     = colorStock(p.stock, p.stock_minimo);
  const pct    = p.stock_minimo > 0 ? Math.min((p.stock / (p.stock_minimo * 3)) * 100, 100) : 50;
  const cv     = colorVenc(p.proximo_vencimiento);
  const dias   = diasHasta(p.proximo_vencimiento);
  const sinStock = p.stock <= 0;

  return (
    <div style={{
      ...S.card(),
      padding:'0',
      overflow:'hidden',
      opacity: sinStock ? .7 : 1,
      transition:'box-shadow .2s, transform .15s',
      cursor:'default',
      display:'flex', flexDirection:'column',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow='0 6px 20px rgba(27,42,94,.14)'; e.currentTarget.style.transform='translateY(-2px)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,.07)';      e.currentTarget.style.transform='none'; }}>

      {/* Cabecera colorida */}
      <div style={{ background: cat.bg, padding:'16px 16px 12px', position:'relative' }}>
        {/* Ícono + controlado badge */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div style={{ fontSize:'28px', lineHeight:1 }}>{cat.emoji}</div>
          <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', justifyContent:'flex-end' }}>
            {p.es_controlado === 1 && (
              <span style={S.badge(C.morado)}>
                🔒 CONTROLADO
                <TooltipAyuda texto="Medicamento de control especial. Requiere número de receta médica y nombre del médico prescriptor para poder dispensarse." />
              </span>
            )}
            {p.categoria && (
              <span style={{ ...S.badge(cat.color, cat.bg), border:`1px solid ${cat.ring}` }}>
                {p.categoria}
              </span>
            )}
          </div>
        </div>
        {/* Nombre */}
        <div style={{ marginTop:'10px' }}>
          <div style={{ fontSize:'14px', fontWeight:'800', color: C.azul, lineHeight:1.2 }}>
            {p.nombre}
          </div>
          <div style={{ fontSize:'11px', color:'#64748b', marginTop:'3px' }}>
            {p.unidad}
            {p.unidad_despacho && p.unidad_despacho !== p.unidad &&
              ` · Despacho: ${p.unidad_despacho}`}
          </div>
          {p.codigo && (
            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'1px' }}>
              #{p.codigo}
            </div>
          )}
        </div>
      </div>

      {/* Cuerpo */}
      <div style={{ padding:'12px 16px', flex:1, display:'flex', flexDirection:'column', gap:'10px' }}>

        {/* Stock */}
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
            <span style={{ fontSize:'11px', color:'#64748b', fontWeight:'600' }}>STOCK DISPONIBLE</span>
            <span style={{ fontSize:'13px', fontWeight:'800', color: cs }}>
              {p.stock} {p.unidad}
            </span>
          </div>
          <div style={{ background:'#f1f5f9', borderRadius:'99px', height:'5px' }}>
            <div style={{ height:'5px', borderRadius:'99px', background: cs,
              width:`${Math.max(pct, sinStock ? 0 : 3)}%`, transition:'width .4s' }} />
          </div>
          {sinStock && (
            <div style={{ fontSize:'10px', color: C.rojo, fontWeight:'700', marginTop:'3px' }}>
              ⛔ SIN STOCK
            </div>
          )}
          {!sinStock && p.stock <= p.stock_minimo && (
            <div style={{ fontSize:'10px', color: C.naranja, fontWeight:'700', marginTop:'3px' }}>
              ⚠️ STOCK BAJO (mín: {p.stock_minimo})
            </div>
          )}
        </div>

        {/* Precios */}
        <div style={{ display:'flex', gap:'8px' }}>
          <div style={{ flex:1, background:'#f8fafd', borderRadius:'8px', padding:'7px 10px', textAlign:'center' }}>
            <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:'700' }}>COSTO PROM.</div>
            <div style={{ fontSize:'13px', fontWeight:'700', color:'#475569' }}>
              {fmtDinero(p.costo_promedio)}
            </div>
          </div>
          <div style={{ flex:1, background: C.azul+'0d', borderRadius:'8px', padding:'7px 10px', textAlign:'center' }}>
            <div style={{ fontSize:'9px', color: C.azul, fontWeight:'700' }}>PRECIO VENTA</div>
            <div style={{ fontSize:'14px', fontWeight:'800', color: C.azul }}>
              {fmtDinero(p.precio_unitario)}
            </div>
          </div>
        </div>

        {/* Vencimiento */}
        {p.proximo_vencimiento && (
          <div style={{ display:'flex', alignItems:'center', gap:'6px',
            background: cv ? cv+'10' : '#f8fafd',
            borderRadius:'7px', padding:'6px 10px',
            border: `1px solid ${cv||'#e2e8f0'}30` }}>
            <span style={{ fontSize:'13px' }}>📅</span>
            <div>
              <div style={{ fontSize:'10px', color:'#94a3b8', fontWeight:'600' }}>PRÓX. VENCIMIENTO</div>
              <div style={{ fontSize:'12px', fontWeight:'700', color: cv || '#64748b' }}>
                {fmtFecha(p.proximo_vencimiento)}
                {dias !== null && (
                  <span style={{ marginLeft:'6px', fontWeight:'400', fontSize:'11px' }}>
                    {dias < 0 ? `(venció hace ${-dias}d)` : `(${dias}d restantes)`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Acciones */}
      <div style={{ padding:'10px 12px', borderTop:'1px solid #f1f5f9', display:'flex', gap:'6px' }}>
        <button
          style={{ ...S.btn(sinStock ? '#e2e8f0' : C.verde, sinStock ? '#94a3b8' : '#fff', '12px'), flex:1 }}
          disabled={sinStock}
          onClick={() => !sinStock && onDespachar(p)}>
          {sinStock ? '⛔ Sin stock' : '💊 Despachar'}
        </button>
        <button
          style={{ ...S.btn('#f1f5f9', '#64748b', '12px'), padding:'9px 12px' }}
          title="Ver Kardex"
          onClick={() => onVerKardex(p)}>
          📋
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  MODAL KARDEX RÁPIDO
// ═════════════════════════════════════════════════════════════
function ModalKardex({ producto, onCerrar }) {
  const [movs, setMovs] = useState([]);
  const [carg, setCarg] = useState(true);

  useEffect(() => {
    api.inventario.kardex({ inventario_id: producto.id, limite: 30 })
      .then(r => { setMovs(r.ok ? r.data : []); setCarg(false); });
  }, [producto.id]);

  const esPos = (m) => ['Entrada', 'Ajuste_Alta', 'Devolución'].includes(m.tipo_mov);

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={{ ...S.card(), width:'720px', maxHeight:'85vh', overflow:'hidden',
        display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: C.azul, padding:'18px 22px', color:'#fff',
          display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:'16px', fontWeight:'800' }}>📋 Kardex — {producto.nombre}</div>
            <div style={{ fontSize:'11px', color:'#93c5fd', marginTop:'2px' }}>
              Stock actual: {producto.stock} {producto.unidad} · Últimos 30 movimientos
            </div>
          </div>
          <button style={{ ...S.btn('rgba(255,255,255,.15)', '#fff', '18px'), padding:'4px 12px' }}
            onClick={onCerrar}>✕</button>
        </div>

        <div style={{ overflowY:'auto', flex:1 }}>
          {carg ? (
            <div style={{ textAlign:'center', padding:'40px', color: C.gris }}>⏳ Cargando...</div>
          ) : movs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color: C.gris }}>Sin movimientos registrados.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafd' }}>
                  {['Fecha','Tipo','Lote','Cantidad','Stock ant.','Stock nuevo','Motivo / Paciente','Usuario'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:'10px',
                      color: C.gris, fontWeight:'700', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {movs.map((m, i) => (
                  <tr key={m.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'8px 12px', color: C.gris, fontSize:'11px', whiteSpace:'nowrap' }}>
                      {m.fecha ? new Date(m.fecha).toLocaleString('es-SV') : '—'}
                    </td>
                    <td style={{ padding:'8px 12px' }}>
                      <span style={S.badge(esPos(m) ? C.verde : C.rojo)}>
                        {esPos(m) ? '↑' : '↓'} {m.tipo_mov}
                      </span>
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:'11px', color: C.azul, fontWeight:'600' }}>
                      {m.numero_lote || '—'}
                    </td>
                    <td style={{ padding:'8px 12px', fontWeight:'800', textAlign:'right',
                      color: esPos(m) ? C.verde : C.rojo }}>
                      {esPos(m) ? '+' : '−'}{m.cantidad}
                    </td>
                    <td style={{ padding:'8px 12px', textAlign:'right', color:'#94a3b8' }}>{m.stock_anterior}</td>
                    <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:'700', color: C.azul }}>{m.stock_nuevo}</td>
                    <td style={{ padding:'8px 12px', fontSize:'11px' }}>
                      {m.motivo_ajuste || m.paciente_nombre || '—'}
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:'11px', color: C.gris }}>{m.usuario_nombre || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  PANEL PENDIENTES — bandeja de Farmacia
//  ① Prescripciones del médico esperando ser dispensadas
//  ② Devoluciones de enfermería esperando confirmación de Farmacia
//     (solo al confirmar se mueve stock/kardex/cuenta)
// ═════════════════════════════════════════════════════════════
function PanelPendientes({ usuario, onActualizar, subInicial }) {
  const [sub,               setSub]               = useState(subInicial || 'prescripciones'); // prescripciones | devoluciones | estacion
  const [prescripciones,    setPrescripciones]    = useState([]);
  const [devoluciones,      setDevoluciones]      = useState([]);
  const [pendientesEstacion,setPendientesEstacion]= useState([]);
  const [estaciones,        setEstaciones]        = useState([]);
  const [cargando,          setCargando]          = useState(true);
  const [modalDispensar,    setModalDispensar]    = useState(null); // prescripción seleccionada
  const [modalDevolucion,   setModalDevolucion]   = useState(null); // devolución seleccionada
  const [modalTransferir,   setModalTransferir]   = useState(null); // prescripción a transferir a estación
  const [procesando,        setProcesando]        = useState(false);
  const [err,               setErr]               = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [rp, rd, re, rest] = await Promise.all([
        api.prescripciones.pendientesFarmacia(),
        api.devoluciones.pendientesFarmacia(),
        api.prescripciones.pendientesEstacion(),
        api.estaciones.listar(),
      ]);
      setPrescripciones(rp?.ok ? rp.data : []);
      setDevoluciones(rd?.ok ? rd.data : []);
      setPendientesEstacion(re?.ok ? re.data : []);
      setEstaciones(rest?.ok ? rest.data : []);
    } catch(_) { setPrescripciones([]); setDevoluciones([]); setPendientesEstacion([]); setEstaciones([]); }
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
      {/* Sub-tabs */}
      <div style={{ display:'flex', gap:'8px' }}>
        <button onClick={() => setSub('prescripciones')}
          style={S.tab(sub === 'prescripciones', C.naranja)}>
          📝 Prescripciones por dispensar
          {prescripciones.length > 0 && (
            <span style={{ marginLeft:'8px', background:'rgba(255,255,255,.25)', padding:'1px 8px', borderRadius:'99px' }}>
              {prescripciones.length}
            </span>
          )}
        </button>
        <button onClick={() => setSub('estacion')}
          style={S.tab(sub === 'estacion', C.azul)}>
          🏥 Transferir a Estación
          {pendientesEstacion.length > 0 && (
            <span style={{ marginLeft:'8px', background:'rgba(255,255,255,.25)', padding:'1px 8px', borderRadius:'99px' }}>
              {pendientesEstacion.length}
            </span>
          )}
        </button>
        <button onClick={() => setSub('devoluciones')}
          style={S.tab(sub === 'devoluciones', C.azulClaro)}>
          ↩ Devoluciones por confirmar
          {devoluciones.length > 0 && (
            <span style={{ marginLeft:'8px', background:'rgba(255,255,255,.25)', padding:'1px 8px', borderRadius:'99px' }}>
              {devoluciones.length}
            </span>
          )}
        </button>
      </div>

      {cargando ? (
        <div style={S.card({ padding:'40px', textAlign:'center', color:C.gris })}>⏳ Cargando pendientes...</div>
      ) : sub === 'prescripciones' ? (
        prescripciones.length === 0 ? (
          <div style={S.card({ padding:'50px', textAlign:'center' })}>
            <div style={{ fontSize:'36px', marginBottom:'10px' }}>✅</div>
            <div style={{ fontSize:'14px', fontWeight:700, color: C.azul }}>Sin prescripciones pendientes</div>
            <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'4px' }}>
              Todas las indicaciones médicas activas ya fueron dispensadas.
            </div>
          </div>
        ) : (
          <div style={S.card({ padding:'0', overflow:'hidden' })}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafd' }}>
                  {['Fecha','Paciente','Hab.','Medicamento','Cant.','Stock','Médico','Receta',''].map(h => (
                    <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:'10px',
                      color: C.gris, fontWeight:'700', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prescripciones.map((p, i) => (
                  <tr key={p.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'9px 12px', whiteSpace:'nowrap', color: C.gris, fontSize:'11px' }}>
                      {new Date(p.creado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                    </td>
                    <td style={{ padding:'9px 12px', fontWeight:700, color: C.azul }}>{p.paciente}</td>
                    <td style={{ padding:'9px 12px' }}>{p.habitacion || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {p.medicamento}
                      {p.es_controlado === 1 && <span style={{ color: C.morado, marginLeft:'4px' }}>🔒</span>}
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:700 }}>{p.cantidad_recetada}</td>
                    <td style={{ padding:'9px 12px', textAlign:'center',
                      color: p.stock < p.cantidad_recetada ? C.rojo : C.verde, fontWeight:700 }}>
                      {p.stock}
                    </td>
                    <td style={{ padding:'9px 12px', fontSize:'11px' }}>
                      {p.medico_nombre ? `${p.medico_nombre} ${p.medico_apellido||''}` : '—'}
                    </td>
                    <td style={{ padding:'9px 12px', fontSize:'11px' }}>{p.num_receta || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      <button style={{ ...S.btn(C.verde,'#fff','11px'), padding:'5px 10px' }}
                        disabled={p.stock <= 0}
                        onClick={() => setModalDispensar(p)}>
                        💊 Dispensar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : sub === 'estacion' ? (
        pendientesEstacion.length === 0 ? (
          <div style={S.card({ padding:'50px', textAlign:'center' })}>
            <div style={{ fontSize:'36px', marginBottom:'10px' }}>✅</div>
            <div style={{ fontSize:'14px', fontWeight:700, color: C.azul }}>Sin prescripciones por transferir</div>
            <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'4px' }}>
              Todo lo recetado ya está disponible en la estación correspondiente.
            </div>
          </div>
        ) : (
          <div style={S.card({ padding:'0', overflow:'hidden' })}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafd' }}>
                  {['Fecha','Paciente','Hab.','Medicamento','Cant.','Stock','Médico','Receta',''].map(h => (
                    <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:'10px',
                      color: C.gris, fontWeight:'700', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendientesEstacion.map((p, i) => (
                  <tr key={p.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'9px 12px', whiteSpace:'nowrap', color: C.gris, fontSize:'11px' }}>
                      {new Date(p.creado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                    </td>
                    <td style={{ padding:'9px 12px', fontWeight:700, color: C.azul }}>{p.paciente}</td>
                    <td style={{ padding:'9px 12px' }}>{p.habitacion || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {p.medicamento}
                      {p.es_controlado === 1 && <span style={{ color: C.morado, marginLeft:'4px' }}>🔒</span>}
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:700 }}>{p.cantidad_recetada}</td>
                    <td style={{ padding:'9px 12px', textAlign:'center',
                      color: p.stock < p.cantidad_recetada ? C.rojo : C.verde, fontWeight:700 }}>
                      {p.stock}
                    </td>
                    <td style={{ padding:'9px 12px', fontSize:'11px' }}>
                      {p.medico_nombre ? `${p.medico_nombre} ${p.medico_apellido||''}` : '—'}
                    </td>
                    <td style={{ padding:'9px 12px', fontSize:'11px' }}>{p.num_receta || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      <button style={{ ...S.btn(C.azul,'#fff','11px'), padding:'5px 10px' }}
                        disabled={p.stock <= 0}
                        onClick={() => setModalTransferir(p)}>
                        🏥 Transferir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        devoluciones.length === 0 ? (
          <div style={S.card({ padding:'50px', textAlign:'center' })}>
            <div style={{ fontSize:'36px', marginBottom:'10px' }}>✅</div>
            <div style={{ fontSize:'14px', fontWeight:700, color: C.azul }}>Sin devoluciones pendientes</div>
            <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'4px' }}>
              No hay solicitudes de devolución esperando confirmación.
            </div>
          </div>
        ) : (
          <div style={S.card({ padding:'0', overflow:'hidden' })}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafd' }}>
                  {['Solicitado','Paciente','Hab.','Medicamento','Cant. solicitada','Motivo','Enfermera',''].map(h => (
                    <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:'10px',
                      color: C.gris, fontWeight:'700', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {devoluciones.map((d, i) => (
                  <tr key={d.id} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                    <td style={{ padding:'9px 12px', whiteSpace:'nowrap', color: C.gris, fontSize:'11px' }}>
                      {new Date(d.solicitado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                    </td>
                    <td style={{ padding:'9px 12px', fontWeight:700, color: C.azul }}>{d.paciente}</td>
                    <td style={{ padding:'9px 12px' }}>{d.habitacion || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {d.medicamento}
                      {d.es_controlado === 1 && <span style={{ color: C.morado, marginLeft:'4px' }}>🔒</span>}
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'center', fontWeight:700 }}>
                      {d.cantidad_solicitada} {d.unidad}
                    </td>
                    <td style={{ padding:'9px 12px', fontSize:'11px', maxWidth:'180px' }}>
                      {d.motivo_enfermera || '—'}
                    </td>
                    <td style={{ padding:'9px 12px', fontSize:'11px' }}>{d.solicitado_por_nombre}</td>
                    <td style={{ padding:'9px 12px' }}>
                      <button style={{ ...S.btn(C.azulClaro,'#fff','11px'), padding:'5px 10px' }}
                        onClick={() => setModalDevolucion(d)}>
                        Revisar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* ── Modal: dispensar prescripción ── */}
      {modalDispensar && (
        <ModalDispensarPrescripcion
          presc={modalDispensar}
          usuario={usuario}
          onCerrar={() => setModalDispensar(null)}
          onListo={() => { setModalDispensar(null); cargar(); onActualizar(); }}
        />
      )}

      {/* ── Modal: transferir prescripción a estación ── */}
      {modalTransferir && (
        <ModalTransferirEstacion
          presc={modalTransferir}
          usuario={usuario}
          estaciones={estaciones}
          onCerrar={() => setModalTransferir(null)}
          onListo={() => { setModalTransferir(null); cargar(); onActualizar(); }}
        />
      )}

      {/* ── Modal: confirmar / rechazar devolución ── */}
      {modalDevolucion && (
        <ModalConfirmarDevolucion
          dev={modalDevolucion}
          usuario={usuario}
          onCerrar={() => setModalDevolucion(null)}
          onListo={() => { setModalDevolucion(null); cargar(); onActualizar(); }}
        />
      )}
    </div>
  );
}

// ── Modal: Farmacia transfiere una prescripción a una estación ──
function ModalTransferirEstacion({ presc, usuario, estaciones, onCerrar, onListo }) {
  const habitacionEstacionId = presc.habitacion_estacion_id || null;
  const [estacionId, setEstacionId] = useState(habitacionEstacionId ? String(habitacionEstacionId) : '');
  const [cantidad,   setCantidad]   = useState(String(presc.cantidad_recetada));
  const [procesando, setProcesando] = useState(false);
  const [err,        setErr]        = useState('');

  const confirmar = async () => {
    setErr('');
    if (!estacionId) { setErr('Selecciona la estación de destino.'); return; }
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setErr('Ingresa una cantidad válida.'); return; }
    if (cant > presc.stock)  { setErr(`Stock insuficiente. Disponible: ${presc.stock} ${presc.unidad}.`); return; }
    setProcesando(true);
    try {
      const r = await api.estaciones.transferir(Number(estacionId), {
        inventario_id: presc.inventario_id, cantidad: cant,
        usuario_id: usuario.id, prescripcion_id: presc.id,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo transferir.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={{ ...S.card(), width:'480px' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azul, padding:'16px 20px', color:'#fff', borderRadius:'12px 12px 0 0' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>🏥 Transferir a estación</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            {presc.paciente} {presc.habitacion ? `· Hab. ${presc.habitacion}` : ''}
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azul }}>{presc.medicamento}</div>
            <div style={{ fontSize:'11px', color: C.gris, marginTop:'2px' }}>
              Recetado: {presc.cantidad_recetada} {presc.unidad} · Stock disponible: {presc.stock} {presc.unidad}
            </div>
            <div style={{ fontSize:'11px', color:'#0f766e', marginTop:'6px' }}>
              El cobro al paciente se generará cuando enfermería administre, no al transferir.
            </div>
          </div>

          <label style={S.label}>Estación destino *</label>
          <select style={S.input} value={estacionId} onChange={e => setEstacionId(e.target.value)}>
            <option value="">Selecciona la estación...</option>
            {estaciones.map(e => (
              <option key={e.id} value={e.id}>{e.nombre}{e.piso_area ? ` — ${e.piso_area}` : ''}</option>
            ))}
          </select>

          <div style={{ marginTop:'12px' }}>
            <label style={S.label}>Cantidad a transferir</label>
            <input style={S.input} type="number" min="0.01" step="any"
              value={cantidad} onChange={e => setCantidad(e.target.value)} />
            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'4px' }}>
              Si la unidad de despacho es mayor a lo recetado (ej. caja de 5 para receta de 3),
              transfiere la unidad completa; el sobrante se devuelve después desde la estación.
            </div>
          </div>

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btn('#f1f5f9', '#475569'), flex:1 }} onClick={onCerrar}>Cancelar</button>
            <button style={{ ...S.btn(C.azul), flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Transfiriendo...' : '✔ Confirmar transferencia'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Farmacia dispensa una prescripción ─────────────────
function ModalDispensarPrescripcion({ presc, usuario, onCerrar, onListo }) {
  const [cantidad,   setCantidad]   = useState(String(presc.cantidad_recetada));
  const [procesando, setProcesando] = useState(false);
  const [err,        setErr]        = useState('');

  const confirmar = async () => {
    setErr('');
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setErr('Ingresa una cantidad válida.'); return; }
    if (cant > presc.stock)  { setErr(`Stock insuficiente. Disponible: ${presc.stock} ${presc.unidad}.`); return; }
    setProcesando(true);
    try {
      const r = await api.prescripciones.dispensar({
        id: presc.id, usuario_id: usuario.id, cantidad_dispensada: cant,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo dispensar.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={{ ...S.card(), width:'480px' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: C.verde, padding:'16px 20px', color:'#fff', borderRadius:'12px 12px 0 0' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>💊 Dispensar prescripción</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            {presc.paciente} {presc.habitacion ? `· Hab. ${presc.habitacion}` : ''}
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azul }}>{presc.medicamento}</div>
            <div style={{ fontSize:'11px', color: C.gris, marginTop:'2px' }}>
              Recetado: {presc.cantidad_recetada} {presc.unidad} · Stock disponible: {presc.stock} {presc.unidad}
            </div>
            {presc.indicaciones && (
              <div style={{ fontSize:'11px', color:'#475569', marginTop:'4px', fontStyle:'italic' }}>
                "{presc.indicaciones}"
              </div>
            )}
            {presc.num_receta && (
              <div style={{ fontSize:'11px', color:'#7c3aed', marginTop:'4px', fontWeight:700 }}>
                🔒 Receta {presc.num_receta} · {presc.dr_prescriptor}
              </div>
            )}
          </div>

          <label style={S.label}>Cantidad a dispensar</label>
          <input style={S.input} type="number" min="0.01" step="any"
            value={cantidad} onChange={e => setCantidad(e.target.value)} />

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btn('#f1f5f9', '#475569'), flex:1 }} onClick={onCerrar}>Cancelar</button>
            <button style={{ ...S.btn(C.verde), flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Procesando...' : '✔ Confirmar despacho'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Farmacia confirma o rechaza una devolución ─────────
// Aquí — y solo aquí — se mueve stock/kardex/cuenta del paciente.
function ModalConfirmarDevolucion({ dev, usuario, onCerrar, onListo }) {
  const [cantidad,    setCantidad]    = useState(String(dev.cantidad_solicitada));
  const [observacion, setObservacion] = useState('');
  const [procesando,  setProcesando]  = useState(false);
  const [err,         setErr]         = useState('');
  const { confirmar: confirmarAccion, ModalConfirmarUI } = useConfirmar();

  const confirmar = async () => {
    setErr('');
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setErr('Ingresa una cantidad válida.'); return; }
    setProcesando(true);
    try {
      const r = await api.devoluciones.confirmar({
        id: dev.id, cantidad_confirmada: cant,
        observacion_farmacia: observacion.trim() || null,
        usuario_id: usuario.id,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo confirmar la devolución.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  const rechazar = async () => {
    if (!(await confirmarAccion({
      texto: '¿Rechazar esta devolución? No se modificará stock ni la cuenta del paciente.',
      tipo: 'advertencia',
      textoConfirmar: 'Rechazar devolución',
    }))) return;
    setErr('');
    setProcesando(true);
    try {
      const r = await api.devoluciones.rechazar({
        id: dev.id, observacion_farmacia: observacion.trim() || 'Rechazada por Farmacia', usuario_id: usuario.id,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo rechazar.');
    } catch(e) { setErr(e.message); }
    setProcesando(false);
  };

  return (
    <div style={S.modal} onClick={onCerrar}>
      {ModalConfirmarUI}
      <div style={{ ...S.card(), width:'480px' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: C.azulClaro, padding:'16px 20px', color:'#fff', borderRadius:'12px 12px 0 0' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>↩ Confirmar devolución</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            {dev.paciente} {dev.habitacion ? `· Hab. ${dev.habitacion}` : ''}
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: C.azul }}>{dev.medicamento}</div>
            <div style={{ fontSize:'11px', color: C.gris, marginTop:'2px' }}>
              Solicitado por {dev.solicitado_por_nombre}: {dev.cantidad_solicitada} {dev.unidad}
            </div>
            {dev.motivo_enfermera && (
              <div style={{ fontSize:'11px', color:'#475569', marginTop:'4px', fontStyle:'italic' }}>
                Motivo: "{dev.motivo_enfermera}"
              </div>
            )}
          </div>

          <label style={S.label}>Cantidad confirmada (verificada físicamente)</label>
          <input style={S.input} type="number" min="0.01" step="any"
            value={cantidad} onChange={e => setCantidad(e.target.value)} />
          <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'4px' }}>
            Si la cantidad recibida no coincide con la solicitada, ajústala aquí — solo esta cantidad se aplicará a stock y a la cuenta del paciente.
          </div>

          <div style={{ marginTop:'12px' }}>
            <label style={S.label}>Observación (opcional)</label>
            <input style={S.input} placeholder="Notas de farmacia sobre esta devolución..."
              value={observacion} onChange={e => setObservacion(e.target.value)} />
          </div>

          {err && <div style={{ color: C.rojo, fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={{ ...S.btn(C.rojo), flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={rechazar}>
              ✕ Rechazar
            </button>
            <button style={{ ...S.btn(C.verde), flex:1, opacity: procesando ? .6 : 1 }}
              disabled={procesando} onClick={confirmar}>
              {procesando ? 'Procesando...' : '✔ Confirmar recepción'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  PANEL DE DESPACHO (POS Farmacia)
// ═════════════════════════════════════════════════════════════
function PanelDespacho({ usuario, productos, onActualizar }) {
  const [tipo,        setTipo]       = useState('interno');
  const [busqMed,     setBusqMed]    = useState('');
  const [busqPac,     setBusqPac]    = useState('');
  const [resPac,      setResPac]     = useState([]);
  const [cargPac,     setCargPac]    = useState(false);
  const [admision,    setAdmision]   = useState(null);
  const [destino,     setDestino]    = useState('');
  const [carrito,     setCarrito]    = useState([]);
  // Campos para medicamentos controlados (receta)
  const [numReceta,     setNumReceta]     = useState('');
  const [drPrescriptor, setDrPrescriptor] = useState('');
  const [despachando, setDespachando]= useState(false);
  const [err,         setErr]        = useState('');
  const [exito,       setExito]      = useState(null);
  const inputRef = useRef();

  // ¿El carrito tiene algún medicamento controlado?
  const hayControlados = useMemo(
    () => carrito.some(i => i.es_controlado),
    [carrito]
  );

  const medsFiltrados = useMemo(() => {
    if (busqMed.length < 2) return [];
    const q = busqMed.toLowerCase();
    return productos.filter(p =>
      p.stock > 0 &&
      (p.nombre.toLowerCase().includes(q) || (p.codigo||'').toLowerCase().includes(q))
    ).slice(0, 8);
  }, [busqMed, productos]);

  const buscarPaciente = async (q) => {
    setBusqPac(q);
    if (q.length < 2) { setResPac([]); return; }
    setCargPac(true);
    try {
      const r = await api.admisiones.buscar(q);
      setResPac((r?.data ?? r ?? []).filter(p => String(p.estado||'').toLowerCase() === 'activa'));
    } catch(_) { setResPac([]); }
    setCargPac(false);
  };

  const selPaciente = (p) => {
    setAdmision(p); setBusqPac(''); setResPac([]); setErr('');
  };

  // Al agregar al carrito, guardamos también es_controlado
  const agregarAlCarrito = (p) => {
    setCarrito(c => {
      const existe = c.find(i => i.producto_id === p.id);
      if (existe) {
        const nueva = existe.cantidad + 1;
        if (nueva > p.stock) {
          setErr(`Stock insuficiente: solo ${p.stock} ${p.unidad} de ${p.nombre}`);
          return c;
        }
        setErr('');
        return c.map(i => i.producto_id === p.id
          ? { ...i, cantidad: nueva, subtotal: nueva * i.precio_unitario }
          : i
        );
      }
      setErr('');
      return [...c, {
        producto_id:    p.id,
        nombre:         p.nombre,
        unidad:         p.unidad,
        cantidad:       1,
        precio_unitario:p.precio_unitario,
        subtotal:       p.precio_unitario,
        stock_max:      p.stock,
        es_controlado:  p.es_controlado === 1,   // ← guardamos el flag
      }];
    });
    setBusqMed('');
    if (inputRef.current) inputRef.current.focus();
  };

  const cambiarCantidad = (prod_id, nueva) => {
    const item = carrito.find(i => i.producto_id === prod_id);
    if (!item) return;
    if (nueva <= 0)             { quitarItem(prod_id); return; }
    if (nueva > item.stock_max) { setErr(`Stock máximo: ${item.stock_max}`); return; }
    setErr('');
    setCarrito(c => c.map(i => i.producto_id === prod_id
      ? { ...i, cantidad: nueva, subtotal: nueva * i.precio_unitario }
      : i
    ));
  };

  const quitarItem  = (id) => setCarrito(c => c.filter(i => i.producto_id !== id));
  const limpiarTodo = () => {
    setCarrito([]); setAdmision(null); setBusqPac('');
    setDestino(''); setErr(''); setNumReceta(''); setDrPrescriptor('');
  };
  const totalCarrito = carrito.reduce((s, i) => s + i.subtotal, 0);

  // ── Confirmar despacho ──────────────────────────────────────
  const confirmarDespacho = async () => {
    setErr('');
    const erroresValidacion = [];

    if (carrito.length === 0)             erroresValidacion.push('El carrito está vacío.');
    if (tipo === 'interno' && !admision)  erroresValidacion.push('Debes seleccionar un paciente hospitalizado.');
    // Validar receta si hay controlados (solo en interno)
    if (tipo === 'interno' && hayControlados) {
      if (!numReceta.trim())     erroresValidacion.push('Hay medicamentos controlados: ingresa el número de receta médica.');
      if (!drPrescriptor.trim()) erroresValidacion.push('Hay medicamentos controlados: ingresa el nombre del médico prescriptor.');
    }

    if (erroresValidacion.length > 0) {
      setErr(erroresValidacion.length === 1
        ? erroresValidacion[0]
        : `Hay ${erroresValidacion.length} puntos por corregir:\n` + erroresValidacion.map(e => `• ${e}`).join('\n'));
      return;
    }
    setDespachando(true);

    const errores    = [];
    const despachados = [];

    for (const item of carrito) {
      try {
        let r;
        if (tipo === 'interno') {
          // ✅ CARGO A CUENTA DEL PACIENTE + descuento FEFO del inventario
          r = await api.cuenta.agregarItem({
            admision_id:    admision.id,
            tipo:           'Medicamento',
            referencia_id:  item.producto_id,
            descripcion:    item.nombre,
            cantidad:       item.cantidad,
            precio_unitario:item.precio_unitario,
            usuario_id:     usuario.id,
            // Receta médica (obligatoria para controlados, opcional para el resto)
            num_receta:     numReceta.trim()     || null,
            dr_prescriptor: drPrescriptor.trim() || null,
          });
        } else {
          // Venta externa: solo descuento de inventario
          r = await api.inventario.ajuste({
            inventario_id: item.producto_id,
            tipo_ajuste:   'Ajuste_Baja',
            cantidad:      item.cantidad,
            motivo_ajuste: `Venta Externa${destino ? ' — ' + destino : ''}`,
            usuario_id:    usuario.id,
          });
        }
        if (!r.ok) errores.push(`${item.nombre}: ${r.error || 'Error desconocido'}`);
        else       despachados.push(item);
      } catch(e) {
        errores.push(`${item.nombre}: ${e.message}`);
      }
    }

    setDespachando(false);
    onActualizar();

    if (errores.length > 0) setErr('⚠️ ' + errores.join(' | '));

    if (despachados.length > 0) {
      setExito({
        tipo,
        paciente:      admision?.paciente || destino || 'Venta Externa',
        habitacion:    admision?.habitacion || '',
        admision_id:   admision?.id || null,
        num_receta:    numReceta || null,
        dr_prescriptor:drPrescriptor || null,
        items:         despachados,
        total:         despachados.reduce((s, i) => s + i.subtotal, 0),
        usuario:       usuario.nombre,
        fecha:         ahora(),
      });
      setCarrito(c => c.filter(i => !despachados.find(d => d.producto_id === i.producto_id)));
      if (despachados.length === carrito.length) {
        setAdmision(null); setDestino(''); setBusqPac('');
        setNumReceta(''); setDrPrescriptor('');
      }
    }
  };

  const puedeConfirmar = carrito.length > 0
    && (tipo === 'externo' || admision)
    && !(tipo === 'interno' && hayControlados && (!numReceta.trim() || !drPrescriptor.trim()));

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 400px', gap:'16px', minHeight:'600px' }}>

      {/* ── Panel Izquierdo ── */}
      <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

        {/* Tipo de despacho */}
        <div style={S.card({ padding:'16px' })}>
          <div style={{ fontSize:'11px', fontWeight:'700', color: C.azul, marginBottom:'10px',
            textTransform:'uppercase', letterSpacing:'.05em' }}>
            Tipo de despacho
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            {[
              { id:'interno', label:'🏥 Uso Interno',   sub:'Cargo a cuenta del paciente', color: C.azul  },
              { id:'externo', label:'🏪 Venta Externa',  sub:'Descuenta inventario directo', color: C.verde },
            ].map(t => (
              <button key={t.id} onClick={() => { setTipo(t.id); setAdmision(null); setErr(''); }}
                style={{ flex:1, padding:'12px 16px', borderRadius:'10px', cursor:'pointer',
                  fontFamily:'inherit', textAlign:'left', transition:'all .15s',
                  border:`2px solid ${tipo===t.id ? t.color : '#e2e8f0'}`,
                  background: tipo===t.id ? t.color+'0d' : '#fff' }}>
                <div style={{ fontSize:'13px', fontWeight:'700', color: tipo===t.id ? t.color : '#475569' }}>
                  {t.label}
                </div>
                <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>{t.sub}</div>
              </button>
            ))}
          </div>

          {/* Aviso claro de lo que hace cada modo */}
          {tipo === 'interno' && (
            <div style={{ marginTop:'10px', background:'#dbeafe', borderRadius:'8px',
              padding:'8px 12px', fontSize:'11px', color:'#1e40af', display:'flex', gap:'6px' }}>
              <span>ℹ️</span>
              <span>
                <strong>Uso Interno:</strong> el medicamento se <strong>descuenta del inventario</strong> y se
                <strong> carga automáticamente a la cuenta del paciente</strong> (visible en Cuenta del Paciente / Facturación).
              </span>
            </div>
          )}
          {tipo === 'externo' && (
            <div style={{ marginTop:'10px', background:'#dcfce7', borderRadius:'8px',
              padding:'8px 12px', fontSize:'11px', color:'#166534', display:'flex', gap:'6px' }}>
              <span>ℹ️</span>
              <span>
                <strong>Venta Externa:</strong> solo se <strong>descuenta del inventario</strong>.
                No genera cargo en ninguna cuenta hospitalaria.
              </span>
            </div>
          )}
        </div>

        {/* Paciente / Destino */}
        <div style={S.card({ padding:'16px' })}>
          {tipo === 'interno' ? (
            <>
              <div style={{ fontSize:'11px', fontWeight:'700', color: C.azul, marginBottom:'10px',
                textTransform:'uppercase', letterSpacing:'.05em' }}>
                🔍 Paciente hospitalizado *
              </div>
              {admision ? (
                <div style={{ display:'flex', alignItems:'center', gap:'10px',
                  background:'#e8f0fb', borderRadius:'8px', padding:'10px 14px' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:'800', color: C.azul, fontSize:'14px' }}>
                      {admision.paciente}
                    </div>
                    <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                      {admision.habitacion ? `🏠 Hab. ${admision.habitacion}` : '🏥 Sin habitación asignada'}
                      {admision.medico ? ` · 🩺 ${admision.medico}` : ''}
                      <span style={{ marginLeft:'8px', ...S.badge(C.verde), fontSize:'9px' }}>
                        Admisión #{admision.id}
                      </span>
                    </div>
                  </div>
                  <button style={S.btn('#fee2e2', C.rojo, '11px')}
                    onClick={() => { setAdmision(null); setBusqPac(''); }}>
                    ✕ Cambiar
                  </button>
                </div>
              ) : (
                <div style={{ position:'relative' }}>
                  <input style={S.input}
                    placeholder="Buscar por nombre, habitación, DUI..."
                    value={busqPac} onChange={e => buscarPaciente(e.target.value)} />
                  {cargPac && <div style={{ fontSize:'11px', color: C.gris, marginTop:'5px' }}>Buscando...</div>}
                  {resPac.length > 0 && (
                    <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200,
                      background:'#fff', border:'1px solid #e2e8f0', borderRadius:'8px',
                      boxShadow:'0 8px 24px rgba(0,0,0,.12)', marginTop:'4px', overflow:'hidden' }}>
                      {resPac.map(p => (
                        <div key={p.id} onClick={() => selPaciente(p)}
                          style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #f1f5f9' }}
                          onMouseEnter={e => e.currentTarget.style.background='#f0f6ff'}
                          onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                          <div style={{ fontWeight:'700', color: C.azul, fontSize:'13px' }}>{p.paciente}</div>
                          <div style={{ fontSize:'11px', color:'#64748b' }}>
                            {p.habitacion ? `Hab. ${p.habitacion} · ` : ''}
                            {p.medico || ''} · Adm. #{p.id}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!cargPac && busqPac.length >= 2 && resPac.length === 0 && (
                    <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'5px' }}>
                      Sin resultados de admisión activa.
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize:'11px', fontWeight:'700', color: C.azul, marginBottom:'10px',
                textTransform:'uppercase', letterSpacing:'.05em' }}>
                📝 Cliente / descripción (opcional)
              </div>
              <input style={S.input} placeholder="Nombre del cliente o nota..."
                value={destino} onChange={e => setDestino(e.target.value)} />
            </>
          )}
        </div>

        {/* Sección de receta — solo si hay controlados en carrito y es interno */}
        {tipo === 'interno' && hayControlados && (
          <div style={S.card({ padding:'16px', border:'2px solid #7c3aed' })}>
            <div style={{ fontSize:'11px', fontWeight:'700', color: C.morado, marginBottom:'10px',
              textTransform:'uppercase', letterSpacing:'.05em' }}>
              🔒 Receta médica — Medicamento(s) controlado(s)
            </div>
            <div style={{ background:'#f5f3ff', borderRadius:'8px', padding:'8px 12px',
              fontSize:'11px', color:'#6d28d9', marginBottom:'12px' }}>
              El carrito contiene medicamentos controlados. La normativa exige número de receta
              y médico prescriptor para su despacho.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div>
                <label style={{ ...S.label, color: C.morado }}>Nº de Receta *</label>
                <input style={{ ...S.input, borderColor: !numReceta.trim() ? C.rojo : '#dde3ea' }}
                  placeholder="Ej: RX-2025-00123"
                  value={numReceta} onChange={e => setNumReceta(e.target.value.toUpperCase())} />
              </div>
              <div>
                <label style={{ ...S.label, color: C.morado }}>Médico Prescriptor *</label>
                <input style={{ ...S.input, borderColor: !drPrescriptor.trim() ? C.rojo : '#dde3ea' }}
                  placeholder="Dr. Nombre Apellido"
                  value={drPrescriptor} onChange={e => setDrPrescriptor(e.target.value.toUpperCase())} />
              </div>
            </div>
          </div>
        )}

        {/* Búsqueda de medicamentos */}
        <div style={{ ...S.card({ padding:'16px' }), flex:1 }}>
          <div style={{ fontSize:'11px', fontWeight:'700', color: C.azul, marginBottom:'10px',
            textTransform:'uppercase', letterSpacing:'.05em' }}>
            💊 Agregar medicamento
          </div>
          <div style={{ position:'relative' }}>
            <input ref={inputRef} style={{ ...S.input, paddingLeft:'36px' }} autoFocus
              placeholder="Buscar por nombre o código (mín. 2 caracteres)..."
              value={busqMed} onChange={e => setBusqMed(e.target.value)} />
            <span style={{ position:'absolute', left:'11px', top:'50%', transform:'translateY(-50%)',
              fontSize:'15px', pointerEvents:'none' }}>🔍</span>
          </div>

          {busqMed.length >= 2 && (
            <div style={{ marginTop:'8px', display:'flex', flexDirection:'column', gap:'4px' }}>
              {medsFiltrados.length === 0 ? (
                <div style={{ textAlign:'center', padding:'20px', color:'#94a3b8', fontSize:'12px' }}>
                  Sin resultados con stock disponible.
                </div>
              ) : medsFiltrados.map(p => {
                const cat = catInfo(p.categoria);
                const enCarrito = carrito.find(i => i.producto_id === p.id);
                return (
                  <div key={p.id}
                    style={{ display:'flex', alignItems:'center', gap:'10px',
                      padding:'8px 12px', borderRadius:'8px', background:'#f8fafd',
                      border:`1px solid ${p.es_controlado===1 ? '#c4b5fd' : '#e2e8f0'}`,
                      cursor:'pointer', transition:'all .1s' }}
                    onClick={() => agregarAlCarrito(p)}
                    onMouseEnter={e => e.currentTarget.style.background='#e8f0fb'}
                    onMouseLeave={e => e.currentTarget.style.background='#f8fafd'}>
                    <span style={{ fontSize:'20px' }}>{cat.emoji}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color: C.azul }}>
                        {p.nombre}
                        {p.es_controlado===1 && (
                          <span style={{ ...S.badge(C.morado), marginLeft:'6px', fontSize:'9px' }}>🔒</span>
                        )}
                      </div>
                      <div style={{ fontSize:'11px', color:'#64748b' }}>
                        {p.unidad} · Stock: <strong style={{ color: colorStock(p.stock, p.stock_minimo) }}>{p.stock}</strong>
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:'14px', fontWeight:'800', color: C.azul }}>
                        {fmtDinero(p.precio_unitario)}
                      </div>
                      {enCarrito && <span style={S.badge(C.verde)}>En carrito: {enCarrito.cantidad}</span>}
                    </div>
                    <span style={{ fontSize:'18px', color: C.verde, fontWeight:'700' }}>＋</span>
                  </div>
                );
              })}
            </div>
          )}

          {busqMed.length < 2 && carrito.length === 0 && (
            <div style={{ textAlign:'center', padding:'40px 20px', color:'#94a3b8' }}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>💊</div>
              <div style={{ fontSize:'13px', fontWeight:'600' }}>Busca un medicamento para iniciar el despacho</div>
              <div style={{ fontSize:'11px', marginTop:'4px' }}>Escribe al menos 2 caracteres del nombre o código</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Panel Derecho: Carrito ── */}
      <div style={{ display:'flex', flexDirection:'column' }}>
        <div style={{ ...S.card(), overflow:'hidden', display:'flex', flexDirection:'column', height:'100%' }}>

          {/* Header */}
          <div style={{ background: tipo==='interno' ? C.azul : C.verde,
            padding:'14px 18px', color:'#fff',
            display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div>
              <div style={{ fontSize:'14px', fontWeight:'800' }}>
                {tipo==='interno' ? '🏥 Despacho Interno' : '🏪 Venta Externa'}
              </div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,.7)', marginTop:'1px' }}>
                {carrito.length} ítem{carrito.length!==1?'s':''}
                {tipo==='interno' && admision && ` · ${admision.paciente}`}
              </div>
            </div>
            {carrito.length > 0 && (
              <button style={S.btn('rgba(255,255,255,.2)', '#fff', '11px')} onClick={limpiarTodo}>
                🗑 Limpiar
              </button>
            )}
          </div>

          {/* Ítems */}
          <div style={{ flex:1, overflowY:'auto', padding:'10px' }}>
            {carrito.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 20px', color:'#94a3b8' }}>
                <div style={{ fontSize:'32px', marginBottom:'8px' }}>🛒</div>
                <div style={{ fontSize:'12px' }}>El carrito está vacío</div>
              </div>
            ) : carrito.map(item => (
              <div key={item.producto_id} style={{ marginBottom:'8px' }}>
                <div style={{ background:'#f8fafd', borderRadius:'8px', padding:'10px 12px',
                  border:`1px solid ${item.es_controlado ? '#c4b5fd' : '#e2e8f0'}` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'8px' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'12px', fontWeight:'700', color: C.azul, lineHeight:1.2 }}>
                        {item.nombre}
                        {item.es_controlado && (
                          <span style={{ ...S.badge(C.morado), marginLeft:'5px', fontSize:'9px' }}>🔒</span>
                        )}
                      </div>
                      <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'1px' }}>
                        {fmtDinero(item.precio_unitario)} c/u · max: {item.stock_max}
                      </div>
                    </div>
                    <button onClick={() => quitarItem(item.producto_id)}
                      style={{ ...S.btn('#fee2e2', C.rojo, '11px'), padding:'3px 8px' }}>✕</button>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'8px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                      <button onClick={() => cambiarCantidad(item.producto_id, item.cantidad - 1)}
                        style={{ ...S.btn('#e2e8f0', '#475569', '14px'), padding:'2px 9px', borderRadius:'6px', fontWeight:'800' }}>−</button>
                      <input type="number" min="1" max={item.stock_max}
                        value={item.cantidad}
                        onChange={e => cambiarCantidad(item.producto_id, parseInt(e.target.value)||1)}
                        style={{ width:'52px', textAlign:'center', padding:'4px', border:'1.5px solid #dde3ea',
                          borderRadius:'6px', fontSize:'13px', fontWeight:'700' }} />
                      <button onClick={() => cambiarCantidad(item.producto_id, item.cantidad + 1)}
                        style={{ ...S.btn(C.verde, '#fff', '14px'), padding:'2px 9px', borderRadius:'6px', fontWeight:'800' }}>＋</button>
                    </div>
                    <div style={{ fontSize:'14px', fontWeight:'800', color: C.azul }}>
                      {fmtDinero(item.subtotal)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding:'14px 16px', borderTop:'2px solid #e2e8f0', background:'#f8fafd', flexShrink:0 }}>

            {/* Resumen destino */}
            {tipo === 'interno' && admision && (
              <div style={{ background:'#dbeafe', borderRadius:'8px', padding:'8px 12px',
                marginBottom:'10px', fontSize:'12px', color:'#1e40af',
                display:'flex', flexDirection:'column', gap:'2px' }}>
                <div style={{ fontWeight:'700' }}>
                  👤 {admision.paciente}
                  {admision.habitacion ? ` — Hab. ${admision.habitacion}` : ''}
                </div>
                <div style={{ fontSize:'10px', color:'#3b82f6' }}>
                  ✅ Se cargará a la cuenta del paciente · Admisión #{admision.id}
                </div>
              </div>
            )}
            {tipo === 'interno' && !admision && carrito.length > 0 && (
              <div style={{ background:'#fee2e2', borderRadius:'8px', padding:'8px 12px',
                marginBottom:'10px', fontSize:'11px', color: C.rojo, fontWeight:'600' }}>
                ⚠️ Selecciona al paciente para poder confirmar el despacho.
              </div>
            )}
            {tipo === 'externo' && (
              <div style={{ background:'#dcfce7', borderRadius:'8px', padding:'8px 12px',
                marginBottom:'10px', fontSize:'11px', color:'#166534' }}>
                🏪 Venta externa{destino ? ` — ${destino}` : ''} · Solo descuenta inventario
              </div>
            )}

            {/* Controlados incompleto */}
            {tipo === 'interno' && hayControlados && (!numReceta.trim() || !drPrescriptor.trim()) && (
              <div style={{ background:'#f5f3ff', borderRadius:'8px', padding:'8px 12px',
                marginBottom:'10px', fontSize:'11px', color: C.morado, fontWeight:'600' }}>
                🔒 Completa los datos de receta médica (panel izquierdo)
              </div>
            )}

            {/* Total */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
              <span style={{ fontSize:'13px', fontWeight:'700', color:'#475569' }}>TOTAL</span>
              <span style={{ fontSize:'22px', fontWeight:'800', color: C.azul }}>{fmtDinero(totalCarrito)}</span>
            </div>

            {err && (
              <div style={{ background:'#fee2e2', color: C.rojo, borderRadius:'7px',
                padding:'8px 11px', fontSize:'11px', marginBottom:'10px', fontWeight:'600',
                lineHeight:1.4, whiteSpace:'pre-line' }}>
                {err}
              </div>
            )}

            <button
              style={{ ...S.btn(puedeConfirmar ? (tipo==='interno'?C.azul:C.verde) : '#e2e8f0',
                       puedeConfirmar ? '#fff' : '#94a3b8'), width:'100%',
                       padding:'12px', fontSize:'14px', borderRadius:'10px' }}
              disabled={despachando || !puedeConfirmar}
              onClick={confirmarDespacho}>
              {despachando
                ? '⏳ Procesando...'
                : tipo === 'interno'
                  ? `🏥 Confirmar — Cargar a Cuenta (${fmtDinero(totalCarrito)})`
                  : `🏪 Confirmar Venta (${fmtDinero(totalCarrito)})`
              }
            </button>
          </div>
        </div>
      </div>

      {exito && <ModalTicket ticket={exito} onCerrar={() => setExito(null)} />}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  MODAL TICKET DE DESPACHO
// ═════════════════════════════════════════════════════════════
function ModalTicket({ ticket, onCerrar }) {
  const imprimir = () => {
    const contenido = `
      <html><head><title>Ticket Farmacia — ALTIMO</title>
      <style>
        body { font-family: monospace; font-size: 12px; margin: 0; padding: 10px; max-width: 320px; }
        h2 { text-align: center; font-size: 16px; margin: 0 0 4px; }
        .sub { text-align: center; font-size: 10px; color: #555; margin-bottom: 12px; }
        hr { border: 1px dashed #aaa; margin: 8px 0; }
        .row { display: flex; justify-content: space-between; margin: 3px 0; }
        .total { font-weight: bold; font-size: 14px; }
        .footer { text-align: center; font-size: 10px; color: #555; margin-top: 12px; }
      </style></head><body>
      <h2>ALTIMO MEDICAL CENTER</h2>
      <div class="sub">Farmacia / Dispensario</div>
      <hr/>
      <div class="row"><span>Fecha:</span><span>${ticket.fecha}</span></div>
      <div class="row"><span>Tipo:</span><span>${ticket.tipo === 'interno' ? 'USO INTERNO' : 'VENTA EXTERNA'}</span></div>
      <div class="row"><span>Paciente/Cliente:</span><span>${ticket.paciente}</span></div>
      ${ticket.habitacion ? `<div class="row"><span>Habitación:</span><span>${ticket.habitacion}</span></div>` : ''}
      <div class="row"><span>Despachado por:</span><span>${ticket.usuario}</span></div>
      <hr/>
      ${ticket.items.map(i => `
        <div class="row">
          <span>${i.nombre}</span>
          <span>${i.cantidad} × ${fmtDinero(i.precio_unitario)}</span>
        </div>
        <div class="row" style="color:#555; font-size:10px">
          <span></span><span>= ${fmtDinero(i.subtotal)}</span>
        </div>
      `).join('')}
      <hr/>
      <div class="row total"><span>TOTAL:</span><span>${fmtDinero(ticket.total)}</span></div>
      <div class="footer">ALTIMO Medical Center — Farmacia<br/>Gracias</div>
      </body></html>
    `;
    const w = window.open('', '_blank', 'width=380,height=600');
    w.document.write(contenido);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  return (
    <div style={S.modal} onClick={onCerrar}>
      <div style={{ ...S.card(), width:'440px', overflow:'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: C.verde, padding:'18px 22px', color:'#fff' }}>
          <div style={{ fontSize:'18px', fontWeight:'800' }}>✅ Despacho Confirmado</div>
          <div style={{ fontSize:'11px', color:'#a7f3d0', marginTop:'2px' }}>{ticket.fecha}</div>
        </div>

        <div style={{ padding:'20px' }}>
          {/* Info */}
          <div style={{ background:'#f0fdf4', borderRadius:'8px', padding:'12px 14px', marginBottom:'16px' }}>
            <div style={{ fontSize:'12px', color:'#064e3b', fontWeight:'700' }}>
              {ticket.tipo === 'interno' ? '🏥 Uso Interno' : '🏪 Venta Externa'}
            </div>
            <div style={{ fontSize:'13px', fontWeight:'700', color: C.azul, marginTop:'4px' }}>
              {ticket.paciente}
            </div>
            {ticket.habitacion && (
              <div style={{ fontSize:'11px', color:'#64748b' }}>Habitación: {ticket.habitacion}</div>
            )}
          </div>

          {/* Ítems */}
          <div style={{ marginBottom:'14px' }}>
            {ticket.items.map((item, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between',
                padding:'7px 0', borderBottom:'1px solid #f1f5f9', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:'600', color: C.azul }}>{item.nombre}</div>
                  <div style={{ fontSize:'11px', color:'#94a3b8' }}>
                    {item.cantidad} {item.unidad} × {fmtDinero(item.precio_unitario)}
                  </div>
                </div>
                <div style={{ fontSize:'14px', fontWeight:'800', color: C.azul }}>
                  {fmtDinero(item.subtotal)}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
            background: C.azul+'0d', borderRadius:'8px', padding:'12px 16px', marginBottom:'16px' }}>
            <span style={{ fontSize:'14px', fontWeight:'700', color: C.azul }}>TOTAL DESPACHADO</span>
            <span style={{ fontSize:'22px', fontWeight:'800', color: C.azul }}>
              {fmtDinero(ticket.total)}
            </span>
          </div>

          <div style={{ fontSize:'11px', color:'#94a3b8', textAlign:'center', marginBottom:'14px' }}>
            Despachado por: <strong>{ticket.usuario}</strong>
            {ticket.tipo === 'interno' && (
              <div style={{ marginTop:'4px', color:'#166534', fontWeight:'600', fontSize:'11px' }}>
                ✅ Cargo registrado en cuenta del paciente (Admisión #{ticket.admision_id})
              </div>
            )}
            {ticket.num_receta && (
              <div style={{ marginTop:'3px', color: C.morado, fontSize:'11px' }}>
                🔒 Receta: {ticket.num_receta} · Dr. {ticket.dr_prescriptor}
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:'8px' }}>
            <button style={{ ...S.btn(C.azul), flex:1 }} onClick={imprimir}>
              🖨 Imprimir Ticket
            </button>
            <button style={{ ...S.btn('#f1f5f9', '#475569'), flex:1 }} onClick={onCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  PANEL HISTORIAL
// ═════════════════════════════════════════════════════════════
function PanelHistorial({ usuario }) {
  const [desde,    setDesde]    = useState(hoy());
  const [hasta,    setHasta]    = useState(hoy());
  const [datos,    setDatos]    = useState([]);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.reportes.kardexRango({ desde, hasta });
      setDatos(r.ok ? r.data : []);
    } catch(_) { setDatos([]); }
    setCargando(false);
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const esPos = (tipo) => ['Entrada', 'Ajuste_Alta', 'Devolución'].includes(tipo);

  const TIPO_COLOR = {
    'Entrada':       C.verde,
    'Ajuste_Alta':   C.verde,
    'Ajuste_Baja':   C.naranja,
    'Venta':         C.azulClaro,
    'Consumo':       C.rojo,
    'Devolución':    C.verde,
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
      {/* Filtros */}
      <div style={S.card({ padding:'16px' })}>
        <div style={{ display:'flex', gap:'12px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div>
            <label style={S.label}>Desde</label>
            <input type="date" style={{ ...S.input, width:'150px' }}
              value={desde} onChange={e => setDesde(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Hasta</label>
            <input type="date" style={{ ...S.input, width:'150px' }}
              value={hasta} onChange={e => setHasta(e.target.value)} />
          </div>
          <button style={S.btn(C.azul)} onClick={cargar}>🔍 Buscar</button>
          <button style={S.btn('#f1f5f9', '#475569')}
            onClick={() => { setDesde(hoy()); setHasta(hoy()); }}>
            Hoy
          </button>
          {datos.length > 0 && (
            <span style={S.badge(C.azul)}>
              {datos.length} movimiento{datos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div style={S.card({ padding:'0', overflow:'hidden' })}>
        {cargando ? (
          <div style={{ textAlign:'center', padding:'60px', color: C.gris }}>⏳ Cargando historial...</div>
        ) : datos.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px', color:'#94a3b8' }}>
            <div style={{ fontSize:'32px', marginBottom:'8px' }}>📋</div>
            <div style={{ fontSize:'13px', fontWeight:'600' }}>Sin movimientos en el rango seleccionado</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
              <thead>
                <tr style={{ background:'#f8fafd' }}>
                  {['Fecha','Medicamento','Tipo','Lote','Cantidad','Costo','Stock nuevo','Destino / Motivo','Usuario'].map(h => (
                    <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:'10px',
                      color: C.gris, fontWeight:'700', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.map((m, i) => (
                  <tr key={m.id} style={{ background: i%2===0?'#fff':'#fafafa',
                    borderLeft:`3px solid ${TIPO_COLOR[m.tipo_mov] || C.gris}` }}>
                    <td style={{ padding:'8px 12px', color: C.gris, fontSize:'11px', whiteSpace:'nowrap' }}>
                      {m.fecha ? new Date(m.fecha).toLocaleString('es-SV') : '—'}
                    </td>
                    <td style={{ padding:'8px 12px', fontWeight:'600', color: C.azul, maxWidth:'160px' }}>
                      <div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {m.nombre_producto || '—'}
                      </div>
                    </td>
                    <td style={{ padding:'8px 12px' }}>
                      <span style={S.badge(TIPO_COLOR[m.tipo_mov] || C.gris)}>
                        {esPos(m.tipo_mov) ? '↑' : '↓'} {m.tipo_mov}
                      </span>
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:'11px', color: C.azul }}>
                      {m.numero_lote || '—'}
                    </td>
                    <td style={{ padding:'8px 12px', fontWeight:'800', textAlign:'right',
                      color: esPos(m.tipo_mov) ? C.verde : C.rojo }}>
                      {esPos(m.tipo_mov) ? '+' : '−'}{m.cantidad}
                    </td>
                    <td style={{ padding:'8px 12px', textAlign:'right', color:'#475569' }}>
                      {m.costo_unitario != null ? fmtDinero(m.costo_unitario) : '—'}
                    </td>
                    <td style={{ padding:'8px 12px', fontWeight:'700', textAlign:'right', color: C.azul }}>
                      {m.stock_nuevo}
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:'11px', color:'#475569', maxWidth:'180px' }}>
                      <div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {m.motivo_ajuste || m.paciente_nombre || m.tipo_referencia || '—'}
                      </div>
                    </td>
                    <td style={{ padding:'8px 12px', fontSize:'11px', color: C.gris }}>
                      {m.usuario_nombre || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  PANEL CONTROLADOS
// ═════════════════════════════════════════════════════════════
function PanelControlados({ productos, usuario }) {
  const [seleccionado, setSeleccionado] = useState(null);
  const [kardex,       setKardex]       = useState([]);
  const [cargKardex,   setCargKardex]   = useState(false);
  const [desde,        setDesde]        = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [hasta, setHasta] = useState(hoy());

  const controlados = useMemo(() =>
    productos.filter(p => p.es_controlado === 1),
  [productos]);

  const cargarKardex = useCallback(async (prod) => {
    if (!prod) return;
    setCargKardex(true);
    const r = await api.inventario.kardex({
      inventario_id: prod.id, desde, hasta, limite: 200,
    });
    setKardex(r.ok ? r.data : []);
    setCargKardex(false);
  }, [desde, hasta]);

  useEffect(() => {
    if (seleccionado) cargarKardex(seleccionado);
  }, [seleccionado, cargarKardex]);

  const esPos = (tipo) => ['Entrada','Ajuste_Alta','Devolución'].includes(tipo);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:'16px' }}>
      {/* Lista controlados */}
      <div style={S.card({ padding:'0', overflow:'hidden' })}>
        <div style={{ background:'#7c3aed', padding:'12px 16px', color:'#fff' }}>
          <div style={{ fontSize:'13px', fontWeight:'800' }}>🔒 Medicamentos Controlados</div>
          <div style={{ fontSize:'11px', color:'#ddd6fe', marginTop:'2px' }}>
            {controlados.length} registrado{controlados.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ overflowY:'auto', maxHeight:'560px' }}>
          {controlados.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#94a3b8', fontSize:'12px' }}>
              No hay medicamentos controlados registrados.
            </div>
          ) : controlados.map(p => {
            const cs = colorStock(p.stock, p.stock_minimo);
            const activo = seleccionado?.id === p.id;
            return (
              <div key={p.id} onClick={() => setSeleccionado(p)}
                style={{ padding:'12px 14px', cursor:'pointer', transition:'background .1s',
                  borderLeft: activo ? `4px solid #7c3aed` : '4px solid transparent',
                  background: activo ? '#f5f3ff' : '#fff',
                  borderBottom:'1px solid #f1f5f9' }}
                onMouseEnter={e => { if (!activo) e.currentTarget.style.background='#faf5ff'; }}
                onMouseLeave={e => { if (!activo) e.currentTarget.style.background='#fff'; }}>
                <div style={{ fontSize:'13px', fontWeight:'700', color: C.azul, lineHeight:1.2 }}>
                  {p.nombre}
                </div>
                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>{p.unidad}</div>
                <div style={{ fontSize:'12px', fontWeight:'800', color: cs, marginTop:'4px' }}>
                  {p.stock} {p.unidad} disponibles
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Kardex del controlado */}
      <div style={S.card({ overflow:'hidden', display:'flex', flexDirection:'column' })}>
        {!seleccionado ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center',
            padding:'60px', color:'#94a3b8', flexDirection:'column', gap:'12px' }}>
            <div style={{ fontSize:'40px' }}>🔒</div>
            <div style={{ fontSize:'13px', fontWeight:'600' }}>
              Selecciona un medicamento controlado para ver su trazabilidad
            </div>
          </div>
        ) : (
          <>
            <div style={{ background:'#7c3aed', padding:'14px 18px', color:'#fff', flexShrink:0 }}>
              <div style={{ fontSize:'15px', fontWeight:'800' }}>
                🔒 {seleccionado.nombre} — Trazabilidad
              </div>
              <div style={{ display:'flex', gap:'12px', marginTop:'10px', flexWrap:'wrap' }}>
                <div>
                  <label style={{ ...S.label, color:'#ddd6fe' }}>Desde</label>
                  <input type="date" style={{ ...S.input, width:'140px', background:'rgba(255,255,255,.15)',
                    color:'#fff', border:'1px solid rgba(255,255,255,.3)' }}
                    value={desde} onChange={e => setDesde(e.target.value)} />
                </div>
                <div>
                  <label style={{ ...S.label, color:'#ddd6fe' }}>Hasta</label>
                  <input type="date" style={{ ...S.input, width:'140px', background:'rgba(255,255,255,.15)',
                    color:'#fff', border:'1px solid rgba(255,255,255,.3)' }}
                    value={hasta} onChange={e => setHasta(e.target.value)} />
                </div>
                <div style={{ alignSelf:'flex-end' }}>
                  <button style={S.btn('rgba(255,255,255,.2)', '#fff', '12px')}
                    onClick={() => cargarKardex(seleccionado)}>
                    🔍 Filtrar
                  </button>
                </div>
              </div>
            </div>
            <div style={{ overflowY:'auto', flex:1 }}>
              {cargKardex ? (
                <div style={{ textAlign:'center', padding:'40px', color: C.gris }}>⏳ Cargando...</div>
              ) : kardex.length === 0 ? (
                <div style={{ textAlign:'center', padding:'40px', color:'#94a3b8', fontSize:'12px' }}>
                  Sin movimientos en el período seleccionado.
                </div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                  <thead>
                    <tr style={{ background:'#f5f3ff' }}>
                      {['Fecha y Hora','Tipo','Lote','Cantidad','Stock anterior','Stock nuevo','Motivo / Paciente','Usuario'].map(h => (
                        <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:'10px',
                          color:'#7c3aed', fontWeight:'700', borderBottom:'2px solid #ede9fe', whiteSpace:'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {kardex.map((m, i) => (
                      <tr key={m.id} style={{ background: i%2===0?'#fff':'#faf5ff' }}>
                        <td style={{ padding:'8px 12px', fontSize:'11px', color: C.gris, whiteSpace:'nowrap' }}>
                          {m.fecha ? new Date(m.fecha).toLocaleString('es-SV') : '—'}
                        </td>
                        <td style={{ padding:'8px 12px' }}>
                          <span style={S.badge(esPos(m.tipo_mov) ? C.verde : C.rojo)}>
                            {esPos(m.tipo_mov) ? '↑' : '↓'} {m.tipo_mov}
                          </span>
                        </td>
                        <td style={{ padding:'8px 12px', fontWeight:'600', color:'#7c3aed', fontSize:'11px' }}>
                          {m.numero_lote || '—'}
                        </td>
                        <td style={{ padding:'8px 12px', fontWeight:'800', textAlign:'right',
                          color: esPos(m.tipo_mov) ? C.verde : C.rojo }}>
                          {esPos(m.tipo_mov) ? '+' : '−'}{m.cantidad}
                        </td>
                        <td style={{ padding:'8px 12px', textAlign:'right', color:'#94a3b8' }}>{m.stock_anterior}</td>
                        <td style={{ padding:'8px 12px', fontWeight:'800', textAlign:'right', color:'#7c3aed' }}>{m.stock_nuevo}</td>
                        <td style={{ padding:'8px 12px', fontSize:'11px', maxWidth:'180px' }}>
                          <div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                            {m.motivo_ajuste || m.paciente_nombre || '—'}
                          </div>
                        </td>
                        <td style={{ padding:'8px 12px', fontSize:'11px', color: C.gris }}>
                          {m.usuario_nombre || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL — FARMACIA
// ═════════════════════════════════════════════════════════════
export default function Farmacia({ usuario, filtroInicial }) {
  const [vista,        setVista]       = useState(filtroInicial?.vista || 'catalogo');
  const [productos,    setProductos]   = useState([]);
  const [cargando,     setCargando]    = useState(false);
  const [busqueda,     setBusqueda]    = useState('');
  const [filtroCat,    setFiltroCat]   = useState('Todos');
  const [filtroAlerta, setFiltroAlerta]= useState(filtroInicial?.alerta || 'Todos');
  const [modalKardex,  setModalKardex] = useState(null);
  const [vistaGrid,    setVistaGrid]   = useState(true); // grid vs tabla

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.inventario.listar();
      setProductos(r.ok ? r.data : []);
    } catch(_) { setProductos([]); }
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── KPIs ──────────────────────────────────────────────────
  const kpis = useMemo(() => ({
    total:      productos.length,
    stockBajo:  productos.filter(p => p.stock > 0 && p.stock <= p.stock_minimo).length,
    sinStock:   productos.filter(p => p.stock <= 0).length,
    porVencer:  productos.filter(p => { const d = diasHasta(p.proximo_vencimiento); return d !== null && d >= 0 && d <= 30; }).length,
    controlados:productos.filter(p => p.es_controlado === 1).length,
  }), [productos]);

  // ── Filtrado catálogo ─────────────────────────────────────
  // Solo mostrar productos cuando hay búsqueda o filtro activo
  const hayFiltroActivo = busqueda.trim().length > 0
    || filtroCat    !== 'Todos'
    || filtroAlerta !== 'Todos';

  const filtrados = useMemo(() => {
    if (!hayFiltroActivo) return [];
    return productos.filter(p => {
      const q    = busqueda.toLowerCase();
      const mBus = !busqueda || p.nombre.toLowerCase().includes(q) || (p.codigo||'').toLowerCase().includes(q);
      const mCat = filtroCat === 'Todos' || p.categoria === filtroCat;
      const mAlt = filtroAlerta === 'Todos'       ? true
                 : filtroAlerta === 'SinStock'    ? p.stock <= 0
                 : filtroAlerta === 'StockBajo'   ? (p.stock > 0 && p.stock <= p.stock_minimo)
                 : filtroAlerta === 'Critico'     ? (p.stock <= 0 || (p.stock > 0 && p.stock <= p.stock_minimo))
                 : filtroAlerta === 'PorVencer'   ? (() => { const d = diasHasta(p.proximo_vencimiento); return d !== null && d >= 0 && d <= 30; })()
                 : filtroAlerta === 'Controlado'  ? p.es_controlado === 1
                 : true;
      return mBus && mCat && mAlt;
    });
  }, [productos, busqueda, filtroCat, filtroAlerta, hayFiltroActivo]);

  const TABS = [
    { id:'catalogo',    label:'🏪 Catálogo',            color: C.azul   },
    { id:'pendientes',  label:'🔔 Pendientes',          color: C.naranja },
    { id:'despacho',    label:'💊 Despacho',             color: C.verde  },
    { id:'historial',   label:'📋 Historial',            color: C.azulClaro },
    { id:'controlados', label:'🔒 Controlados',          color: C.morado },
  ];

  const ALERTAS_FILTRO = [
    { id:'Todos',      label:'Todos',              count: kpis.total },
    { id:'Critico',    label:'🔴 Stock Crítico',   count: kpis.sinStock + kpis.stockBajo, color: C.rojo },
    { id:'SinStock',   label:'⛔ Sin Stock',       count: kpis.sinStock,   color: C.rojo    },
    { id:'StockBajo',  label:'⚠️ Stock Bajo',      count: kpis.stockBajo,  color: C.naranja },
    { id:'PorVencer',  label:'📅 Por Vencer (30d)',count: kpis.porVencer,  color: C.naranja },
    { id:'Controlado', label:'🔒 Controlados',     count: kpis.controlados,color: C.morado  },
  ];

  const tabActivo = TABS.find(t => t.id === vista) || TABS[0];

  return (
    <div style={S.page}>
      {/* ══ HEADER ══ */}
      <div style={{ background: `linear-gradient(135deg, ${C.azul} 0%, #2d4a8a 100%)`,
        padding:'20px 24px 0', color:'#fff' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
          marginBottom:'16px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <div style={{ fontSize:'22px', fontWeight:'800', letterSpacing:'-.02em' }}>
              💊 Farmacia / Dispensario
            </div>
            <div style={{ fontSize:'12px', color:'#93c5fd', marginTop:'2px' }}>
              ALTIMO Medical Center · {new Date().toLocaleDateString('es-SV', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
            </div>
          </div>
          <button style={{ ...S.btn('rgba(255,255,255,.15)', '#fff', '12px') }}
            onClick={cargar} disabled={cargando}>
            {cargando ? '⏳' : '🔄'} {cargando ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>

        {/* KPIs */}
        <div style={{ display:'flex', gap:'10px', marginBottom:'0', flexWrap:'wrap' }}>
          {[
            { label:'Total productos',  val: kpis.total,       color:'#93c5fd', bg:'rgba(255,255,255,.1)' },
            { label:'⛔ Sin stock',      val: kpis.sinStock,    color:'#fca5a5', bg:'rgba(220,53,69,.25)',  ocultar: kpis.sinStock===0   },
            { label:'⚠️ Stock bajo',     val: kpis.stockBajo,   color:'#fbbf24', bg:'rgba(230,126,34,.25)', ocultar: kpis.stockBajo===0  },
            { label:'📅 Por vencer',     val: kpis.porVencer,   color:'#fbbf24', bg:'rgba(230,126,34,.25)', ocultar: kpis.porVencer===0  },
            { label:'🔒 Controlados',   val: kpis.controlados, color:'#c4b5fd', bg:'rgba(124,58,237,.25)'  },
          ].filter(k => !k.ocultar).map((k, i) => (
            <div key={i} style={{ background: k.bg, borderRadius:'8px', padding:'8px 14px',
              backdropFilter:'blur(4px)', border:`1px solid ${k.color}30` }}>
              <div style={{ fontSize:'20px', fontWeight:'800', color: k.color }}>{k.val}</div>
              <div style={{ fontSize:'10px', color: k.color, opacity:.85 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:'4px', marginTop:'16px' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setVista(t.id)}
              style={{ ...S.tab(vista===t.id, t.color), borderRadius:'8px 8px 0 0',
                padding:'10px 20px', fontSize:'13px' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ CONTENIDO ══ */}
      <div style={{ padding:'20px 24px' }}>

        {/* ─── CATÁLOGO ─── */}
        {vista === 'catalogo' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

            {/* Barra de herramientas */}
            <div style={S.card({ padding:'14px 16px' })}>
              <div style={{ display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' }}>
                {/* Búsqueda */}
                <div style={{ flex:1, minWidth:'200px', position:'relative' }}>
                  <input style={{ ...S.input, paddingLeft:'36px' }}
                    placeholder="Buscar medicamento por nombre o código..."
                    value={busqueda} onChange={e => setBusqueda(e.target.value)} autoFocus />
                  <span style={{ position:'absolute', left:'11px', top:'50%', transform:'translateY(-50%)',
                    fontSize:'15px', pointerEvents:'none' }}>🔍</span>
                </div>

                {/* Categoría */}
                <select style={{ ...S.input, width:'160px' }}
                  value={filtroCat} onChange={e => setFiltroCat(e.target.value)}>
                  <option value="Todos">Todas las categorías</option>
                  {Object.keys(CATS).map(c => (
                    <option key={c} value={c}>{CATS[c].emoji} {c}</option>
                  ))}
                </select>

                {/* Toggle vista */}
                <div style={{ display:'flex', borderRadius:'8px', overflow:'hidden',
                  border:'1.5px solid #e2e8f0' }}>
                  <button onClick={() => setVistaGrid(true)}
                    style={{ ...S.btn(vistaGrid ? C.azul : '#fff', vistaGrid ? '#fff' : '#64748b', '14px'),
                      padding:'7px 12px', borderRadius:0 }}>⊞</button>
                  <button onClick={() => setVistaGrid(false)}
                    style={{ ...S.btn(!vistaGrid ? C.azul : '#fff', !vistaGrid ? '#fff' : '#64748b', '14px'),
                      padding:'7px 12px', borderRadius:0 }}>☰</button>
                </div>

                {cargando && <span style={{ color: C.gris, fontSize:'12px' }}>⏳ Cargando...</span>}
                {hayFiltroActivo && (
                  <span style={{ ...S.badge(C.azul), fontSize:'11px' }}>
                    {filtrados.length} resultado{filtrados.length!==1?'s':''}
                  </span>
                )}
                {!hayFiltroActivo && (
                  <span style={{ ...S.badge('#64748b'), fontSize:'11px' }}>
                    {kpis.total} en catálogo
                  </span>
                )}
              </div>

              {/* Filtros de alerta */}
              <div style={{ display:'flex', gap:'6px', marginTop:'10px', flexWrap:'wrap' }}>
                {ALERTAS_FILTRO.map(a => (
                  <button key={a.id} onClick={() => setFiltroAlerta(a.id)}
                    style={{ padding:'4px 12px', borderRadius:'20px', fontSize:'11px', fontWeight:'600',
                      cursor:'pointer', border:`1.5px solid ${filtroAlerta===a.id ? (a.color||C.azul) : '#e2e8f0'}`,
                      background: filtroAlerta===a.id ? (a.color||C.azul)+'18' : '#fff',
                      color: filtroAlerta===a.id ? (a.color||C.azul) : '#64748b',
                      fontFamily:'inherit', transition:'all .15s' }}>
                    {a.label}
                    {a.count > 0 && (
                      <span style={{ marginLeft:'5px', background: (a.color||C.azul)+'25',
                        padding:'1px 6px', borderRadius:'99px' }}>
                        {a.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid / Tabla de productos */}
            {!hayFiltroActivo ? (
              /* Estado inicial: sin búsqueda activa */
              <div style={{ ...S.card({ padding:'70px 40px', textAlign:'center' }) }}>
                <div style={{ fontSize:'52px', marginBottom:'14px' }}>💊</div>
                <div style={{ fontSize:'18px', fontWeight:'800', color: C.azul, marginBottom:'6px' }}>
                  Catálogo de Farmacia
                </div>
                <div style={{ fontSize:'13px', color:'#94a3b8', maxWidth:'380px', margin:'0 auto 24px' }}>
                  Escribe el nombre o código del medicamento en el campo de búsqueda,
                  o usa los filtros rápidos para encontrar lo que necesitas.
                </div>
                {/* Accesos rápidos a filtros de alerta */}
                <div style={{ display:'flex', gap:'10px', justifyContent:'center', flexWrap:'wrap' }}>
                  {[
                    { id:'SinStock',  label:'⛔ Sin Stock',       count: kpis.sinStock,    color: C.rojo    },
                    { id:'StockBajo', label:'⚠️ Stock Bajo',      count: kpis.stockBajo,   color: C.naranja },
                    { id:'PorVencer', label:'📅 Por vencer',      count: kpis.porVencer,   color: C.naranja },
                    { id:'Controlado',label:'🔒 Controlados',     count: kpis.controlados, color: C.morado  },
                  ].map(a => (
                    <button key={a.id} onClick={() => setFiltroAlerta(a.id)}
                      style={{ padding:'10px 18px', borderRadius:'10px', cursor:'pointer',
                        border:`2px solid ${a.color}30`, background: a.color+'0d',
                        color: a.color, fontWeight:'700', fontSize:'12px', fontFamily:'inherit',
                        transition:'all .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background=a.color+'20'; e.currentTarget.style.borderColor=a.color; }}
                      onMouseLeave={e => { e.currentTarget.style.background=a.color+'0d'; e.currentTarget.style.borderColor=a.color+'30'; }}>
                      {a.label}
                      {a.count > 0 && (
                        <span style={{ marginLeft:'7px', background: a.color+'25',
                          padding:'2px 8px', borderRadius:'99px', fontSize:'11px' }}>
                          {a.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : filtrados.length === 0 ? (
              <div style={{ ...S.card({ padding:'60px', textAlign:'center' }) }}>
                <div style={{ fontSize:'40px', marginBottom:'12px' }}>🔍</div>
                <div style={{ fontSize:'15px', fontWeight:'700', color: C.azul }}>
                  Sin resultados
                </div>
                <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'4px' }}>
                  Intenta otra búsqueda o ajusta los filtros.
                </div>
              </div>
            ) : vistaGrid ? (
              <div style={{ display:'grid',
                gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap:'14px' }}>
                {filtrados.map(p => (
                  <TarjetaMed key={p.id} p={p}
                    onDespachar={(prod) => { setVista('despacho'); }}
                    onVerKardex={(prod) => setModalKardex(prod)} />
                ))}
              </div>
            ) : (
              /* Vista tabla */
              <div style={S.card({ padding:'0', overflow:'hidden' })}>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                    <thead>
                      <tr style={{ background:'#f8fafd' }}>
                        {['','Medicamento','Categoría','Stock','Mín.','Costo prom.','Px Venta','Próx. Vence','Acciones'].map(h => (
                          <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontSize:'10px',
                            color: C.gris, fontWeight:'700', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtrados.map((p, i) => {
                        const cat = catInfo(p.categoria);
                        const cs  = colorStock(p.stock, p.stock_minimo);
                        const cv  = colorVenc(p.proximo_vencimiento);
                        const pct = p.stock_minimo > 0
                          ? Math.min((p.stock / (p.stock_minimo * 3)) * 100, 100) : 50;
                        return (
                          <tr key={p.id} style={{ background: i%2===0?'#fff':'#fafafa' }}
                            onMouseEnter={e => e.currentTarget.style.background='#f0f6ff'}
                            onMouseLeave={e => e.currentTarget.style.background= i%2===0?'#fff':'#fafafa'}>
                            <td style={{ padding:'8px 12px', textAlign:'center', fontSize:'20px' }}>
                              {cat.emoji}
                            </td>
                            <td style={{ padding:'8px 12px' }}>
                              <div style={{ fontWeight:'700', color: C.azul, fontSize:'13px' }}>
                                {p.nombre}
                                {p.es_controlado===1 && (
                                  <span style={{ ...S.badge(C.morado), marginLeft:'6px', fontSize:'9px' }}>
                                    🔒
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize:'11px', color:'#94a3b8' }}>{p.unidad}</div>
                            </td>
                            <td style={{ padding:'8px 12px' }}>
                              <span style={{ padding:'2px 8px', borderRadius:'99px', fontSize:'10px',
                                fontWeight:'700', background: cat.bg, color: cat.color }}>
                                {p.categoria || '—'}
                              </span>
                            </td>
                            <td style={{ padding:'8px 12px', minWidth:'100px' }}>
                              <div style={{ fontWeight:'800', color: cs, fontSize:'13px' }}>{p.stock}</div>
                              <div style={{ background:'#f1f5f9', borderRadius:'99px', height:'4px', marginTop:'3px' }}>
                                <div style={{ height:'4px', borderRadius:'99px', background: cs,
                                  width:`${Math.max(pct, p.stock<=0?0:3)}%` }} />
                              </div>
                            </td>
                            <td style={{ padding:'8px 12px', color:'#94a3b8', fontSize:'12px' }}>{p.stock_minimo}</td>
                            <td style={{ padding:'8px 12px', color:'#475569' }}>{fmtDinero(p.costo_promedio)}</td>
                            <td style={{ padding:'8px 12px', fontWeight:'800', color: C.azul }}>
                              {fmtDinero(p.precio_unitario)}
                            </td>
                            <td style={{ padding:'8px 12px' }}>
                              {p.proximo_vencimiento ? (
                                <span style={{ color: cv || C.gris, fontWeight:'600', fontSize:'12px' }}>
                                  {fmtFecha(p.proximo_vencimiento)}
                                </span>
                              ) : <span style={{ color:'#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ padding:'8px 12px' }}>
                              <div style={{ display:'flex', gap:'4px' }}>
                                <button style={{ ...S.btn(C.verde, '#fff', '11px'), padding:'5px 10px' }}
                                  disabled={p.stock <= 0}
                                  onClick={() => setVista('despacho')}>
                                  💊 Despachar
                                </button>
                                <button style={{ ...S.btn('#f1f5f9', '#475569', '11px'), padding:'5px 10px' }}
                                  onClick={() => setModalKardex(p)}>
                                  📋
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── PENDIENTES (prescripciones por dispensar + devoluciones por confirmar) ─── */}
        {vista === 'pendientes' && (
          <PanelPendientes usuario={usuario} onActualizar={cargar} subInicial={filtroInicial?.sub} />
        )}

        {/* ─── DESPACHO ─── */}
        {vista === 'despacho' && (
          <PanelDespacho
            usuario={usuario}
            productos={productos}
            onActualizar={cargar}
          />
        )}

        {/* ─── HISTORIAL ─── */}
        {vista === 'historial' && (
          <PanelHistorial usuario={usuario} />
        )}

        {/* ─── CONTROLADOS ─── */}
        {vista === 'controlados' && (
          <PanelControlados productos={productos} usuario={usuario} />
        )}
      </div>

      {/* ─── MODAL KARDEX ─── */}
      {modalKardex && (
        <ModalKardex
          producto={modalKardex}
          onCerrar={() => setModalKardex(null)}
        />
      )}
    </div>
  );
}
